import React, { useContext, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import CredentialsContext from "../context/CredentialsContext";
import "../styles/credentialPage.css";
import CredentialForm from "./CredentialForm.js";
import { useAuth } from "../context/AuthContext";
import {
    deleteCredential as deleteCredentialFromApi,
    extendCredentialExpiration,
} from "../services/api";
import {
    expirationLabels,
    formatExpirationDate,
    getDaysUntilExpiration,
    getExpirationStatus,
} from "../utils/expiration";
import { buildPublicAppUrl } from "../services/publicAppUrl";

export default function CredentialPage({
    method = "view",
    credentialState = null,
}) {
    const navigate = useNavigate();
    const { credentialID } = useParams();
    const { credentials, loading, error, refresh } = useContext(CredentialsContext);
    const [credential, setCredential] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [copyStatus, setCopyStatus] = useState("");
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const [showExtendExpiration, setShowExtendExpiration] = useState(false);
    const [extensionDuration, setExtensionDuration] = useState("30-days");
    const [extensionSaving, setExtensionSaving] = useState(false);
    const [extensionError, setExtensionError] = useState(null);

    // added local input state
    const [successful, setSuccessful] = useState(false);
    const { role } = useAuth();

    //get the details of the credential when the credential ID from the URL or context changes
    useEffect(() => {
        if (loading) return;
        const foundCredential = credentials.find(
            (item) => String(item.id) === String(credentialID)
        );
        setCredential(foundCredential ?? null);
        setSuccessful(foundCredential?.["login-successful"] ?? false);
        if (foundCredential?.duration) {
            setExtensionDuration(foundCredential.duration);
        }
    }, [credentialID, credentials, loading]);

    if (loading || error || !credential) return (
        <main className="create-credential">
            <Link className="back-link" to="/">← All credentials</Link>
            <div className="vault-empty" role={error ? "alert" : "status"}>
                <h1>{loading ? "Loading credential…" : error ? "Unable to load credentials" : "Credential not found"}</h1>
                {!loading && <p>{error ? "Check your connection and try again." : "It may have been removed, or you may not have access."}</p>}
                {error && <button className="btn" onClick={refresh}>Try again</button>}
            </div>
        </main>
    );

    const expirationStatus = getExpirationStatus(credential.expires_at);
    const expirationLabel = expirationLabels[expirationStatus] || "Expiration";
    const formattedExpiration = formatExpirationDate(credential.expires_at);
    const daysRemaining = getDaysUntilExpiration(credential.expires_at);
    const expirationDisplay =
        expirationStatus === "no-expiration"
            ? "No expiration"
            : formattedExpiration
              ? `Expires ${formattedExpiration}`
              : "Expiration date unavailable";
    const showExpirationWarning =
        expirationStatus === "expired" || expirationStatus === "expiring-soon";
    const isInternal = role === "internal_staff";
    const canExtendExpiration = isInternal && Boolean(credential.expires_at);
    const clientName = [credential.first_name, credential.last_name]
        .filter(Boolean)
        .join(" ");

    function toggleEditMode() {
        setEditMode((prev) => !prev);
    }

    async function handleDeleteCredential() {
        if (deleting) return;
        if (
            window.confirm(
                "Delete this credential? This cannot be undone and will remove the password too."
            ) !== true
        ) {
            return;
        }

        try {
            setDeleteError(null);
            setDeleting(true);
            await deleteCredentialFromApi(credentialID);
            await refresh();
            navigate("/");
        } catch (err) {
            console.error("Failed to delete credential", err);
            setDeleteError(err.message || "Unable to delete credential.");
            setDeleting(false);
        }
    }

    async function handleExtendExpiration() {
        if (!credential?.id || extensionSaving) return;
        try {
            setExtensionError(null);
            setExtensionSaving(true);
            const updated = await extendCredentialExpiration(
                credential.id,
                extensionDuration
            );
            setCredential(updated);
            await refresh();
            setShowExtendExpiration(false);
        } catch (err) {
            console.error("Failed to extend expiration", err);
            setExtensionError(err.message || "Unable to extend expiration.");
        } finally {
            setExtensionSaving(false);
        }
    }

    return (
        <div className="credential-card">
            <div className="credential-page-toolbar">
                <Link className="back-link" to="/">← All credentials</Link>
                <div className="credential-page-buttons">
                    <button type="button" className="pill-btn pill-secondary" onClick={async () => {
                        try {
                            await navigator.clipboard.writeText(buildPublicAppUrl(`/credential/${credentialID}`));
                            setCopyStatus("Link copied. Paste it into CRM.");
                        } catch { setCopyStatus("Couldn’t copy. Copy the page address from your browser instead."); }
                    }}>Copy page link</button>
                    {!editMode && <button type="button" className="pill-btn pill-secondary" onClick={toggleEditMode}>Edit credential</button>}
                    {!editMode && <button type="button" className="pill-btn delete-action" disabled={deleting} onClick={handleDeleteCredential}>
                        {deleting ? "Deleting…" : "Delete"}
                    </button>}
                </div>
            </div>
            {copyStatus && <p className="copy-status" role="status">{copyStatus}</p>}
            {deleteError && <p className="error-message">{deleteError}</p>}

            <h1>{credential.company}</h1>
            <h2 className="credential-provider">{credential.provider}</h2>
            <div className="expiration-details">
                <div className="expiration-summary">
                    <span
                        className={`expiration-badge expiration-status ${expirationStatus}`}
                        aria-label={`Expiration status: ${expirationLabel}`}
                    >
                        {expirationLabel}
                    </span>
                    <span className="expiration-date">{expirationDisplay}</span>
                    {daysRemaining !== null &&
                        expirationStatus !== "no-expiration" && (
                            <span className="expiration-days">
                                {daysRemaining > 0
                                    ? `${daysRemaining} days remaining`
                                    : `${Math.abs(daysRemaining)} days overdue`}
                            </span>
                        )}
                </div>
                {credential.duration && (
                    <p className="expiration-meta">
                        Duration: {credential.duration.replace("-", " ")}
                    </p>
                )}
                {clientName && (
                    <p className="expiration-meta">Client: {clientName}</p>
                )}
                {credential.comments && (
                    <p className="expiration-meta">
                        Notes: {credential.comments}
                    </p>
                )}
            </div>
            {showExpirationWarning && (
                <div className="expiration-warning warning container">
                    <h3>Expiration alert</h3>
                    <p className="error-message">
                        {expirationStatus === "expired"
                            ? "This credential has expired and may need to be refreshed."
                            : "This credential will expire soon. Consider extending it."}
                    </p>
                </div>
            )}
            {!successful && (
                <div className="login-failed-warning warning container">
                    <h3>Warning</h3>
                    <p className="error-message">
                        This credential wasn't accepted last time it was used.
                        We may need to collect this from the user again.
                    </p>
                </div>
            )}
            {canExtendExpiration && (
                <div className="extend-expiration">
                    <button
                        className="btn extend-expiration-button"
                        onClick={() =>
                            setShowExtendExpiration((prev) => !prev)
                        }
                    >
                        Extend Expiration
                    </button>
                    {showExtendExpiration && (
                        <div className="extend-expiration-form">
                            <label htmlFor="extend-duration">
                                New duration
                            </label>
                            <select
                                id="extend-duration"
                                value={extensionDuration}
                                onChange={(e) =>
                                    setExtensionDuration(e.target.value)
                                }
                            >
                                <option value="1-week">1 week</option>
                                <option value="30-days">30 days</option>
                                <option value="until-complete">
                                    Until complete
                                </option>
                            </select>
                            <button
                                className="btn"
                                disabled={extensionSaving}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleExtendExpiration();
                                }}
                            >
                                {extensionSaving ? "Updating..." : "Confirm"}
                            </button>
                            <button
                                className="btn cancel-btn"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setShowExtendExpiration(false);
                                }}
                            >
                                Cancel
                            </button>
                            {extensionError && (
                                <p className="error-message">
                                    {extensionError}
                                </p>
                            )}
                        </div>
                    )}
                </div>
            )}
            <CredentialForm
                editMode={editMode}
                setEditMode={setEditMode}
                credential={credential}
                setCredential={setCredential}
                method={editMode ? "edit" : "view"}
                successful={successful}
                setSuccessful={setSuccessful}
            />
        </div>
    );
}
